// writeQueue.ts — IDB-backed write queue for critical flight-safety data.
//
// Problem: affEvents (drop_start / drop_end), dip-site-uses, and briefing
// snapshots are written directly to Supabase. In a cellular dead zone,
// any Supabase insert fails and the data is silently lost. A regulatory
// auditor cannot pull the exact events the pilot logged.
//
// Solution: write to IDB FIRST, then attempt the Supabase write.
// On success → remove from queue. On failure → leave in queue.
// On 'online' event (and on sign-in) → drain the queue by replaying.
//
// Idempotency is guaranteed by the existing client-side ID plumbing:
//   - aff_events.client_id + partial unique index (AC3/XO3)
//   - app.dip_site_uses.client_id (column pre-existed)
//   - flight_briefings.id (deterministic briefingId, upsert on conflict)
// So replay is always safe, even if the write already succeeded but we
// didn't get the ack (e.g. network dropped between server commit and
// response).
//
// Usage:
//   import { enqueueWrite, drainQueue } from './writeQueue';
//   // In a write function: enqueue before the Supabase call; on success
//   // the entry is acked and removed. drainQueue() is called automatically
//   // on 'online' events.

import { kvGet, kvSet } from './idb';
import { onSignIn } from './authEvents';

// ── Types ─────────────────────────────────────────────────────────────────

export type QueueEntryType =
  | 'aff_event'
  | 'dip_site_use'
  | 'briefing_snapshot'
  | 'frat_item_update'
  | 'frat_assessment_update';

export interface QueueEntry {
  /** Stable client-side id. Used as the dedup key. */
  id: string;
  type: QueueEntryType;
  /** ISO timestamp when this entry was enqueued. */
  enqueuedAt: string;
  /** Attempt count (capped at MAX_ATTEMPTS). */
  attempts: number;
  /** Serialized write payload — passed back to the replay handler. */
  payload: unknown;
}

// ── IDB persistence ────────────────────────────────────────────────────────

const IDB_KEY = 'fyrefly:writeQueue:v1';
const MAX_ENTRIES = 500;   // Safety cap so IDB can't grow unbounded.
const MAX_ATTEMPTS = 10;   // Give up after 10 consecutive failures.

let _queue: QueueEntry[] = [];
let _loaded = false;
let _loading = false;

async function loadQueue(): Promise<QueueEntry[]> {
  if (_loaded) return _queue;
  if (_loading) {
    // Wait for in-progress load.
    await new Promise<void>((r) => { const t = setInterval(() => { if (_loaded) { clearInterval(t); r(); } }, 50); });
    return _queue;
  }
  _loading = true;
  const stored = await kvGet<QueueEntry[]>(IDB_KEY);
  _queue = Array.isArray(stored) ? stored : [];
  _loaded = true;
  _loading = false;
  return _queue;
}

async function persistQueue(): Promise<void> {
  await kvSet(IDB_KEY, _queue);
}

// ── Public API ─────────────────────────────────────────────────────────────

/** Add a write to the queue. No-op if an entry with the same id already exists. */
export async function enqueueWrite(entry: Omit<QueueEntry, 'attempts'>): Promise<void> {
  const q = await loadQueue();
  if (q.some((e) => e.id === entry.id)) return; // Already queued.
  q.push({ ...entry, attempts: 0 });
  // Trim to cap.
  if (q.length > MAX_ENTRIES) _queue = q.slice(-MAX_ENTRIES);
  await persistQueue();
}

/** Acknowledge a successful write — remove from queue. */
async function ackWrite(id: string): Promise<void> {
  const q = await loadQueue();
  const idx = q.findIndex((e) => e.id === id);
  if (idx === -1) return;
  q.splice(idx, 1);
  await persistQueue();
}

// ── Replay handlers ────────────────────────────────────────────────────────

// Registered lazily to avoid circular imports. Each write module registers
// its own handler via registerReplayHandler().
const _replayHandlers = new Map<QueueEntryType, (entry: QueueEntry) => Promise<boolean>>();

/**
 * Register a replay handler for a queue entry type.
 * The handler should return true on success, false on failure.
 * Should be called once at module initialization (top-level import side-effect).
 */
export function registerReplayHandler(
  type: QueueEntryType,
  handler: (entry: QueueEntry) => Promise<boolean>,
): void {
  _replayHandlers.set(type, handler);
}

// ── Drain ─────────────────────────────────────────────────────────────────

let _draining = false;

/**
 * Drain the queue: attempt to replay all pending writes.
 * Called automatically on 'online' event and when called by sign-in handlers.
 * Safe to call redundantly — only one drain runs at a time.
 */
export async function drainQueue(): Promise<void> {
  if (_draining) return;
  if (!navigator.onLine) return;
  _draining = true;
  try {
    const q = await loadQueue();
    // Copy so we can mutate _queue during iteration.
    const snapshot = [...q];
    for (const entry of snapshot) {
      const handler = _replayHandlers.get(entry.type);
      if (!handler) continue;
      try {
        const ok = await handler(entry);
        if (ok) {
          await ackWrite(entry.id);
        } else {
          // Increment attempt count; give up after MAX_ATTEMPTS.
          entry.attempts += 1;
          if (entry.attempts >= MAX_ATTEMPTS) {
            // eslint-disable-next-line no-console
            console.warn('[writeQueue] giving up on', entry.id, 'after', entry.attempts, 'attempts');
            await ackWrite(entry.id); // Remove from queue (don't retry forever).
          } else {
            await persistQueue();
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[writeQueue] replay error for', entry.id, e);
        entry.attempts += 1;
        await persistQueue();
      }
    }
  } finally {
    _draining = false;
  }
}

// ── Connectivity listener ─────────────────────────────────────────────────

// Wire up the 'online' event and sign-in event once at module load.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    void drainQueue();
  });
  // Drain on sign-in so writes that failed when the user was offline
  // are retried as soon as they have an auth session again.
  onSignIn(() => { void drainQueue(); });
}

// ── Queue size (for debugging / settings page) ────────────────────────────

/** Return the number of pending entries (after IDB load). */
export async function queueSize(): Promise<number> {
  const q = await loadQueue();
  return q.length;
}
